import React, { useState, useEffect } from "react";
import { getFirestore, collection, getDocs, orderBy, query, doc, deleteDoc, setDoc } from "firebase/firestore";
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { StyledTableCell, StyledTableRow } from '../../utils/styled';

const firestore = getFirestore();

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function ListServicios() {
    const [servicios, setServicios] = useState([]);
    const [preciosMap, setPreciosMap] = useState({});
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [serviceToDelete, setServiceToDelete] = useState(null);
    const [snackbarInfo, setSnackbarInfo] = useState({ text: '', type: '', open: false });

    // Modal state for Monto Fijo
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedService, setSelectedService] = useState(null);
    const [montoInput, setMontoInput] = useState('');
    const [montoFeriadoInput, setMontoFeriadoInput] = useState('');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const qServices = query(collection(firestore, "servicios"), orderBy("id", "asc"));
            const snapshotServices = await getDocs(qServices);
            const servicesData = snapshotServices.docs.map(docSnap => ({ ...docSnap.data(), docId: docSnap.id }));

            const qPrices = query(collection(firestore, "precios"), orderBy("id", "asc"));
            const snapshotPrices = await getDocs(qPrices);
            const pMap = {};
            snapshotPrices.docs.forEach(docSnap => {
                const data = docSnap.data();
                if (data.id !== undefined) {
                    pMap[data.id] = data;
                }
            });

            setServicios(servicesData);
            setPreciosMap(pMap);
        } catch (error) {
            console.error("Error al cargar servicios y precios:", error);
            setSnackbarInfo({ text: 'Error al cargar servicios', type: 'error', open: true });
        }
    };

    const handleSwitchChange = async (servicio, currentMontoFijo) => {
        const servicioId = servicio.id;
        const priceInfo = preciosMap[servicioId] || {};

        if (!currentMontoFijo) {
            // Turning ON -> open modal
            setSelectedService({ id: servicioId, name: servicio.name || priceInfo.descripcion });
            const defaultSemana = priceInfo.montoFijoValor || priceInfo.hogar || '';
            const defaultFeriado = priceInfo.montoFijoFeriadoValor || priceInfo.feriado_domingo_hogar || defaultSemana;
            setMontoInput(defaultSemana);
            setMontoFeriadoInput(defaultFeriado);
            setModalOpen(true);
        } else {
            // Turning OFF -> set montoFijo false
            try {
                const priceDocRef = doc(firestore, 'precios', String(servicioId));
                await setDoc(priceDocRef, { montoFijo: false }, { merge: true });

                setPreciosMap(prev => ({
                    ...prev,
                    [servicioId]: { ...prev[servicioId], montoFijo: false }
                }));

                setSnackbarInfo({
                    open: true,
                    text: `Desactivado monto fijo para el servicio`,
                    type: 'info'
                });
            } catch (error) {
                console.error("Error al desactivar montoFijo:", error);
                setSnackbarInfo({ open: true, text: 'Error al actualizar el tipo de precio', type: 'error' });
            }
        }
    };

    const handleEditMontoValue = (servicio, priceInfo) => {
        setSelectedService({ id: servicio.id, name: servicio.name });
        const defaultSemana = priceInfo.montoFijoValor || priceInfo.hogar || '';
        const defaultFeriado = priceInfo.montoFijoFeriadoValor || priceInfo.feriado_domingo_hogar || defaultSemana;
        setMontoInput(defaultSemana);
        setMontoFeriadoInput(defaultFeriado);
        setModalOpen(true);
    };

    const handleSaveModal = async () => {
        if (!selectedService) return;
        const priceInfo = preciosMap[selectedService.id] || {};
        const valToSave = montoInput.trim() || priceInfo.hogar || '0';
        const valFeriadoToSave = montoFeriadoInput.trim() || priceInfo.feriado_domingo_hogar || valToSave;

        try {
            const priceDocRef = doc(firestore, 'precios', String(selectedService.id));
            await setDoc(priceDocRef, {
                montoFijo: true,
                montoFijoValor: String(valToSave),
                montoFijoFeriadoValor: String(valFeriadoToSave)
            }, { merge: true });

            setPreciosMap(prev => ({
                ...prev,
                [selectedService.id]: {
                    ...prev[selectedService.id],
                    montoFijo: true,
                    montoFijoValor: String(valToSave),
                    montoFijoFeriadoValor: String(valFeriadoToSave)
                }
            }));

            setSnackbarInfo({
                open: true,
                text: `"${selectedService.name}": Monto Fijo establecido en $ ${formatNumber(valToSave)} (D/F: $ ${formatNumber(valFeriadoToSave)})`,
                type: 'success'
            });

            setModalOpen(false);
            setSelectedService(null);
        } catch (error) {
            console.error("Error al guardar montoFijoValor:", error);
            setSnackbarInfo({ open: true, text: 'Error al guardar el monto fijo', type: 'error' });
        }
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedService(null);
    };

    const handleClose = () => {
        setOpenDeleteModal(false);
    };

    const deleteService = async () => {
        if (!serviceToDelete) return;
        try {
            const serviceDocRef = doc(firestore, 'servicios', String(serviceToDelete.id));
            await deleteDoc(serviceDocRef);

            try {
                const priceDocRef = doc(firestore, 'precios', String(serviceToDelete.id));
                await deleteDoc(priceDocRef);
            } catch (err) {
                console.warn("No se pudo eliminar el precio correspondiente:", err);
            }

            setSnackbarInfo({ text: 'Servicio eliminado correctamente!', type: 'success', open: true });
            setOpenDeleteModal(false);
            loadData();
        } catch (error) {
            console.error('Error al eliminar servicio:', error);
            setOpenDeleteModal(false);
            setSnackbarInfo({ text: 'Ocurrió un error al eliminar', type: 'error', open: true });
        }
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarInfo({ text: '', type: 'info', open: false });
    };

    const formatNumber = (val) => {
        if (val === undefined || val === null || val === '') return '0';
        const num = Number(val);
        if (isNaN(num)) return `${val}`;
        return `${num.toLocaleString('es-PY')}`;
    };

    return (
        <Container component="main" maxWidth="xl">
            <Typography variant="h5" sx={{ marginTop: '20px', marginBottom: '10px' }}>Catálogo de Servicios</Typography>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <Button variant="contained" color="primary" href="/add-servicio">Nuevo Servicio</Button>
            </div>
            <TableContainer>
                <Table aria-label="tabla servicios">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Id</StyledTableCell>
                            <StyledTableCell>Nombre</StyledTableCell>
                            <StyledTableCell>Tipo</StyledTableCell>
                            <StyledTableCell>Orden</StyledTableCell>
                            <StyledTableCell>Precio Cliente</StyledTableCell>
                            <StyledTableCell>Feriado/Domingo</StyledTableCell>
                            <StyledTableCell align="center">Monto Fijo Cuadrilla</StyledTableCell>
                            <StyledTableCell>Acciones</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {servicios && servicios.map((servicio) => {
                            const priceInfo = preciosMap[servicio.id] || {};
                            const isFixed = priceInfo.montoFijo === true || priceInfo.montoFijo === "true";
                            const fixedVal = priceInfo.montoFijoValor || priceInfo.hogar || '0';
                            const fixedValFeriado = priceInfo.montoFijoFeriadoValor || priceInfo.feriado_domingo_hogar || fixedVal;
                            return (
                                <StyledTableRow key={servicio.id}>
                                    <StyledTableCell>{servicio.id}</StyledTableCell>
                                    <StyledTableCell>{servicio.name}</StyledTableCell>
                                    <StyledTableCell>{servicio.type || 'check'}</StyledTableCell>
                                    <StyledTableCell>{servicio.order !== undefined ? servicio.order : '-'}</StyledTableCell>
                                    <StyledTableCell>$ {formatNumber(priceInfo.hogar)}</StyledTableCell>
                                    <StyledTableCell>$ {formatNumber(priceInfo.feriado_domingo_hogar)}</StyledTableCell>
                                    <StyledTableCell align="center">
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={isFixed}
                                                        onChange={() => {
                                                            setSelectedService(servicio);
                                                            handleSwitchChange(servicio, isFixed);
                                                        }}
                                                        color="primary"
                                                    />
                                                }
                                                label={isFixed ? `$ ${formatNumber(fixedVal)} (D/F: $ ${formatNumber(fixedValFeriado)})` : "% Participación"}
                                            />
                                            {isFixed && (
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleEditMontoValue(servicio, priceInfo)}
                                                    title="Editar Monto Fijo"
                                                >
                                                    <EditOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </StyledTableCell>
                                    <StyledTableCell className="column-actions">
                                        <IconButton
                                            aria-label="edit"
                                            className="action__link"
                                            href={`/servicio/${servicio.id}`}
                                            role="button"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            aria-label="delete"
                                            type="button"
                                            className="action__button"
                                            onClick={() => {
                                                setOpenDeleteModal(true);
                                                setServiceToDelete(servicio);
                                            }}
                                        >
                                            <DeleteIcon sx={{ color: 'red' }} />
                                        </IconButton>
                                    </StyledTableCell>
                                </StyledTableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Modal para ingresar/modificar Monto Fijo */}
            <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="xs" fullWidth>
                <DialogTitle>Establecer Monto Fijo Cuadrilla</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Ingrese los montos fijos en Gs. que se pagarán a la cuadrilla por el servicio: <strong>{selectedService?.name}</strong>.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="montoFijoInput"
                        label="Monto Fijo Semana (Gs.)"
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={montoInput}
                        onChange={(e) => setMontoInput(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        id="montoFijoFeriadoInput"
                        label="Monto Fijo Domingo / Feriado (Gs.)"
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={montoFeriadoInput}
                        onChange={(e) => setMontoFeriadoInput(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal} color="inherit">
                        Cancelar
                    </Button>
                    <Button onClick={handleSaveModal} variant="contained" color="primary">
                        Guardar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openDeleteModal}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {`¿Desea eliminar el servicio "${serviceToDelete?.name}"?`}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Se eliminará el servicio y su precio asociado. Esta acción no se puede deshacer.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancelar</Button>
                    <Button onClick={deleteService} color="error" autoFocus>
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbarInfo.open} autoHideDuration={3000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={handleCloseSnackbar} severity={snackbarInfo.type} sx={{ width: '100%' }}>
                    {snackbarInfo.text}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default ListServicios;
